/**
 * useStarterModal.ts
 *
 * Pure selector hook — reads from AppContext central store only.
 * No Firestore calls. No useRef guards. No async effects.
 *
 * The seen-map is loaded once by AppContext after profile hydration.
 * Screens must not decide whether to show a modal until starterModalsLoaded
 * is true — this prevents any open-then-close flicker during hydration.
 *
 * Dismiss calls markStarterSeen(screen) which:
 *   1. Updates AppContext state immediately — modal hides at once
 *   2. Persists to Firestore in the background — survives logout/reinstall
 *   3. Never waits for Firestore before hiding the modal
 *
 * Route changes and remounts are safe — the seen-map lives in AppContext,
 * not in hook-local state, so it survives unmount/remount cycles.
 *
 * Target: src/hooks/useStarterModal.ts
 */

import { useAppContext } from '../store/AppContext';
import type { StarterModalScreen } from '../services/userProfile.service';

export type { StarterModalScreen };

interface UseStarterModalResult {
  isOpen: boolean;
  dismiss: () => void;
}

/**
 * @param screen   - Which screen this modal belongs to
 * @param eligible - Caller-supplied gate (e.g. onboardingComplete, glp1Enabled).
 *                   Modal will not open unless this is true AND the screen
 *                   has never been seen AND starterModalsLoaded is true.
 */
export function useStarterModal(
  screen: StarterModalScreen,
  eligible: boolean,
): UseStarterModalResult {
  const { starterModalsLoaded, starterModalsSeen, markStarterSeen } = useAppContext();

  // Do not show until the seen-map is confirmed from Firestore.
  // Prevents flicker where the modal briefly opens then closes.
  if (!starterModalsLoaded) {
    return { isOpen: false, dismiss: () => {} };
  }

  // Do not show if caller says not eligible
  if (!eligible) {
    return { isOpen: false, dismiss: () => {} };
  }

  // Do not show if this screen has already been seen
  if (starterModalsSeen[screen] === true) {
    return { isOpen: false, dismiss: () => {} };
  }

  // Eligible and not yet seen — show the modal
  return {
    isOpen: true,
    dismiss: () => markStarterSeen(screen),
  };
}
